import { StarsButton, Translation } from '@ps/web-shared-components';
import React from 'react';
import { useSelector } from 'react-redux';

import useSearchParams from '@swf/core/hooks/useSearchParams';
import type { RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import { FormattedCurrencyAmount, useDateFormatter } from '@swf/poker';

import { NAVIGATION_DATA } from '../../../../constants';
import { usePokerSpinAndGoWidget } from '../../../../model';
import { GAME_VARIANT_TYPE_MAP } from '../../../../types/Game';
import type { FeaturedTournament } from '../../../../types/WatchGames';
import { multiplierHelper } from '../../../../utils/numbers';
import Avatar from '../Avatar/Avatar';

import {
  TXTCLI_Buy_In,
  TXTCLI_Date,
  TXTCLI_Game_Type,
  TXTCLI_Multiplier,
  TXTCLI_Player,
  TXTCLI_Prize,
  TXTCLI_Replay,
  TXTCLI_Something_went_wrongX_please_t_ELL
} from './RecentBigWins.messages.json';
import styles from './RecentBigWins.scss';

interface RecentBigWinsProps {
  winners: FeaturedTournament[];
}

interface RecentRowProps {
  winningUser: FeaturedTournament;
}

interface SearchParams {
  widgetId: string;
}

const RecentRow: React.FC<RecentRowProps> = ({ winningUser }) => {
  const {
    winner,
    imageId,
    prizePool,
    tournEndTime,
    currency,
    winAmount,
    tournId,
    buyIn,
    gameVariant
  } = winningUser;
  const { widgetId } = useSearchParams<SearchParams>();
  const { widgetSelect } = usePokerSpinAndGoWidget(widgetId);
  const isEmbedded = useSelector(select.device.isEmbedded);
  const isPlayMoney = useSelector(select.currency.isPlayMoney);
  const gameType = GAME_VARIANT_TYPE_MAP[gameVariant];
  const gameTypeTitle = NAVIGATION_DATA[gameType]?.title;
  const userImage = useSelector((state: RootState) => widgetSelect.userImage(state, imageId));

  const onClickReplay = () =>
    window.starsweb?.spinandgo?.replay({
      isHof: false,
      isFinalHandOnly: false,
      tournId,
      isPlayMoney
    });
  const multiplier = multiplierHelper({
    buyIn,
    prizePool,
    isPlayMoney
  });

  const date = useDateFormatter({
    timestamp: tournEndTime * 1000,
    reference: Date.now()
  });

  const playerName = encodeURIComponent(winner);

  return (
    <tr>
      <td>
        <Avatar avatar={userImage} userName={playerName} size="small" />
      </td>
      <td className={styles.player}>{playerName}</td>
      <Translation tagName="td" {...gameTypeTitle} />
      <td>
        <FormattedCurrencyAmount
          amount={buyIn}
          isPlayMoney={isPlayMoney}
          currency={currency}
          notationPM="compact"
          currencyDisplay="narrowSymbol"
        />
      </td>
      <td>x{multiplier}</td>
      <td className={styles.prize}>
        <FormattedCurrencyAmount
          amount={winAmount}
          isPlayMoney={isPlayMoney}
          currency={currency}
          notationPM="compact"
          currencyDisplay="narrowSymbol"
        />
      </td>
      <td>
        <time>{date}</time>
      </td>
      {isEmbedded && (
        <td>
          <StarsButton
            size="small"
            className={styles.replayButton}
            translation={TXTCLI_Replay}
            variant="secondary"
            onClick={onClickReplay}
            inverse
          />
        </td>
      )}
    </tr>
  );
};

const RecentBigWins: React.FC<RecentBigWinsProps> = ({ winners }) => {
  const isEmbedded = useSelector(select.device.isEmbedded);

  if (!winners.length) {
    return (
      <div data-testid="empty-container" className={styles.empty}>
        <Translation {...TXTCLI_Something_went_wrongX_please_t_ELL} />
      </div>
    );
  }

  return (
    <div className={styles.recentWins}>
      <table className={styles.recentTable}>
        <thead>
          <tr>
            <th> </th>
            <th className={styles.player}>{<Translation {...TXTCLI_Player} />}</th>
            <th>{<Translation {...TXTCLI_Game_Type} />}</th>
            <th>{<Translation {...TXTCLI_Buy_In} />}</th>
            <th>{<Translation {...TXTCLI_Multiplier} />}</th>
            <th>{<Translation {...TXTCLI_Prize} />}</th>
            <th>{<Translation {...TXTCLI_Date} />}</th>
            {isEmbedded && <th />}
          </tr>
        </thead>
        <tbody>
          {winners.map((winner) => (
            <RecentRow key={winner.tournId} winningUser={winner} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default RecentBigWins;
